import React from "react";
import {
  TabContent,
  LogSection,
  SectionTitle,
  ErrorMessage,
} from "./LogContentDisplay.styles";
import { JSONRenderer } from "./JSONRenderer/JSONRenderer";
import { LogMetadata } from "./LogMetadata/LogMetadata";
import { QuestionAndSectionsRenderer } from "./QuestionAndSectionsRenderer/QuestionAndSectionsRenderer";
import { ResponseDisplay } from "./ResponseDisplay/ResponseDisplay";
import {
  parseJSON,
  parseNestedJSON,
  parseRelevantIndices,
} from "../../../../../helpers/json-helpers";

// Main component
export const LogContentDisplay = ({ logData, t, selectedMacroId, selectedMacroSource, selectedMacro }) => {
  if (!logData) return <TabContent>{t("logs.noLogData")}</TabContent>;

  try {
    // Use the enhanced deep JSON parser for prompts
    const promptData = parseNestedJSON(logData.prompt);
    const responseData = parseNestedJSON(logData.response);

    // Special handling for macros step
    if (logData.step === "findMacrosSuggestions") {
      let macrosData = [...(responseData.suggestions || [])];
      const product = promptData.product || responseData.product || null;
      const currentQuestion = promptData.currentQuestion || null;

      // If macro was selected from search and not in suggestions, add it to the beginning
      if (selectedMacroId && selectedMacroSource === "search" && selectedMacro) {
        const macroInSuggestions = macrosData.find(m => m.id === selectedMacroId);
        if (!macroInSuggestions) {
          // Add selected macro at the beginning with a special flag
          macrosData = [{ ...selectedMacro, isSelectedFromSearch: true }, ...macrosData];
        }
      }

      return (
        <TabContent>
          <LogSection>
            <SectionTitle>{t("logs.currentQuestion")}</SectionTitle>
            <div style={{ padding: "12px", background: "#f5f5f5", borderRadius: "4px", marginBottom: "16px" }}>
              {currentQuestion || t("logs.noQuestion")}
            </div>
          </LogSection>

          {product && (
            <LogSection>
              <SectionTitle>{t("logs.product")}</SectionTitle>
              <div style={{ padding: "12px", background: "#f5f5f5", borderRadius: "4px", marginBottom: "16px" }}>
                {product}
              </div>
            </LogSection>
          )}

          <LogSection>
            <SectionTitle>{t("logs.macrosSuggestions")} ({macrosData.length})</SectionTitle>
            {macrosData.length > 0 ? (
              <div>
                {macrosData.map((macro, index) => {
                  const isSelected = selectedMacroId && macro.id === selectedMacroId;
                  const isFromSearch = macro.isSelectedFromSearch || false;

                  return (
                    <div
                      key={index}
                      style={{
                        marginBottom: "16px",
                        padding: "16px",
                        background: isSelected ? "#f0fdf4" : "#ffffff",
                        border: isSelected ? "2px solid #10b981" : "1px solid #e0e0e0",
                        borderRadius: "8px",
                        position: "relative"
                      }}
                    >
                    {isSelected && (
                      <div style={{
                        position: "absolute",
                        top: "12px",
                        right: "12px",
                        background: "#10b981",
                        color: "white",
                        padding: "4px 12px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: 600
                      }}>
                        ✓ {isFromSearch ? t("logs.fromSearch") : t("logs.fromSuggestion")}
                      </div>
                    )}

                    <div style={{ marginBottom: "12px" }}>
                      <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
                        {t("logs.macroTitle")}
                      </div>
                      <div style={{ fontWeight: 600, fontSize: "16px", color: isSelected ? "#047857" : "#1F1F1F" }}>
                        {macro.title}
                      </div>
                    </div>

                    {macro.product && (
                      <div style={{ marginBottom: "12px" }}>
                        <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
                          {t("logs.macroProduct")}
                        </div>
                        <div style={{ fontSize: "14px" }}>
                          {macro.product}
                        </div>
                      </div>
                    )}

                    <div style={{ marginBottom: "12px" }}>
                      <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
                        {t("logs.macroText")}
                      </div>
                      <div style={{ fontSize: "14px", lineHeight: "1.6" }}>
                        {macro.answer}
                      </div>
                    </div>

                    {macro.questions && macro.questions.length > 0 && (
                      <div style={{ marginBottom: "12px" }}>
                        <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
                          {t("logs.macroQuestions")}
                        </div>
                        <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
                          {macro.questions.map((question, qIndex) => (
                            <li key={qIndex} style={{ fontSize: "14px", marginBottom: "4px" }}>
                              {question}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {macro.similarity !== undefined && macro.similarity !== null && (
                      <div>
                        <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
                          {t("logs.macroSimilarity")}
                        </div>
                        <div style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: macro.similarity > 0.7 ? "#059669" : macro.similarity > 0.5 ? "#d97706" : "#dc2626"
                        }}>
                          {(macro.similarity * 100).toFixed(1)}%
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ padding: "12px", background: "#f5f5f5", borderRadius: "4px", color: "#666" }}>
                {t("logs.noResults")}
              </div>
            )}
          </LogSection>

          <LogMetadata logData={logData} t={t} />
        </TabContent>
      );
    }

    // Check if this is a prompt with messages
    const hasMessages = promptData && Array.isArray(promptData.messages);
    const isValidationStep = logData.step === "validateQuestionSimilarity";

    // Find developer message if it exists
    const developerMessage = hasMessages
      ? promptData.messages.find((msg) => msg.role === "developer")
      : null;

    // Find user message with content
    const userMessage = hasMessages
      ? promptData.messages.find((msg) => msg.role === "user")
      : null;

    // Apply the deep parser to user message content if it exists
    const userData = userMessage ? parseNestedJSON(userMessage.content) : null;

    // For other steps, we need to check for question and sections
    const hasQuestionSections = userData && Array.isArray(userData.sections);

    // For validation step, we need clientQuestion and candidateQuestions
    const hasValidationData =
      isValidationStep &&
      userData &&
      Array.isArray(userData.candidateQuestions);

    const relevantIndices = parseRelevantIndices(logData.response);

    // If this is a validation step, add response data to userData
    if (isValidationStep && hasValidationData && responseData) {
      userData.isSimilar = responseData.isSimilar;
      userData.bestMatchIndex = responseData.bestMatchIndex;
      userData.explanation = responseData.explanation;
    }

    return (
      <TabContent>
        <LogSection>
          <SectionTitle>{t("logs.prompt")}</SectionTitle>
          {hasMessages ? (
            <QuestionAndSectionsRenderer
              data={userData}
              developerMessage={developerMessage}
              relevantIndices={relevantIndices}
              isValidation={hasValidationData}
              messages={promptData.messages}
              t={t}
            />
          ) : (
            <JSONRenderer data={promptData} />
          )}
        </LogSection>

        <LogSection>
          <SectionTitle>{t("logs.response")}</SectionTitle>
          <ResponseDisplay response={logData.response} t={t} />
        </LogSection>

        <LogMetadata logData={logData} t={t} />
      </TabContent>
    );
  } catch (error) {
    return (
      <TabContent>
        <LogSection>
          <SectionTitle>{t("logs.error")}</SectionTitle>
          <ErrorMessage>{error.message}</ErrorMessage>
          <SectionTitle>{t("logs.rawData")}</SectionTitle>
          <JSONRenderer
            data={typeof logData === "string" ? parseJSON(logData) : logData}
          />
        </LogSection>
      </TabContent>
    );
  }
};
